/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-20 10:04:45
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import { SubmissionError } from 'redux-form';

import { getStoreDate, getUTCDate } from 'src/helpers/date-time-helper';

/**
 * Init collections expand status
 *
 * @param  {array} rules list of rule
 *
 * @return {object}       expand status for each rule
 */
export const initCollectionsExpandStatus = rules => {
  let expandStatus = {};
  for (const rule of rules) {
    const { id } = rule;
    expandStatus[id] = false;
  }

  return expandStatus;
};

/**
 * Reorder the rules
 *
 * @param  {array} rules       list of rules
 * @param  {number} startIndex start index
 * @param  {number} endIndex   end index
 *
 * @return {array}             list of updated rules
 */
export const reorder = (rules, startIndex, endIndex) => {
  const moveDown = startIndex < endIndex;

  // Get rule order of end index
  const newOrderValue = rules[endIndex].order;

  // Move
  const [removed] = rules.splice(startIndex, 1);
  rules.splice(endIndex, 0, removed);

  // Update order
  const updatedList = rules.map((item, index) => {
    if (moveDown) {
      if (index >= startIndex && index < endIndex) {
        item.order = item.order - 1;
      }
      if (index === endIndex) {
        item.order = newOrderValue;
      }
    } else {
      if (index === endIndex) {
        item.order = newOrderValue;
      }
      if (index > endIndex && index <= startIndex) {
        item.order = item.order + 1;
      }
    }
    return item;
  });

  return updatedList;
};

/**
 * Validate form data before saving
 *
 * @param  {object} merchandising merchandising rule
 * @param  {array} ruleNames      list of rule name
 *                                [
 *                                  { id: string, name: string }
 *                                ]
 * @param {i18n} I18n
 *
 * @return {boolean}              true | false
 */
export const validateDataBeforeSave = (merchandising, ruleNames, i18n) => {
  const { id, name = [], conditions } = merchandising;

  let error = {};

  // Rule name must be unique
  const rule = _.find(ruleNames, { name });
  if (rule && rule.id !== id) {
    error = _.assign(error, {
      name: i18n.translate('AdminHTML.Merchandising.Helper.validateDataBeforeSave_Error1')
    });
  }

  // Throw error
  if (!_.isEmpty(error)) {
    throw new SubmissionError(error);
  }

  if (!conditions || _.isEmpty(conditions)) {
    error = _.assign(error, { conditions: i18n.translate('AdminHTML.Merchandising.Helper.validateDataBeforeSave_Error2') });
  }

  return error;
};

export const isRelatedToDateAttribute = type => ['created_at', 'published_at'].includes(type);
export const isRelatedToSelect2Values = type => ['product_type', 'tags'].includes(type);
export const isRelatedToTextValues = type => ['title', 'vendor', 'price'].includes(type);

/**
 * Initial merchandising after reading from DB
 *
 * @param  {object} merchandising
 * @return {object}
 */
export const initialMerchandising = merchandising => {
  const { collections = [], disjunctive, conditions = [] } = merchandising;

  // Rebuild collections
  const collectionsList = collections.map(collection => {
    // If reading from DB
    if (collection.id) {
      const { id, title, handle } = collection;
      return {
        value: id.toString(),
        label: title,
        handle
      };
    }

    return collection;
  });

  // Convert date string to date object in conditions
  const conditionsList = conditions.map(condition => {
    const { field, relation, value } = condition;
    if (isRelatedToDateAttribute(field) && !_.isDate(value)) {
      // Get store date from date value
      const dateObj = getStoreDate(value);

      return { field, relation, value: dateObj };
    }

    // Convert to select object value
    if (isRelatedToSelect2Values(field) && !_.isObject(value)) {
      return { field, relation, value: { value, label: value } };
    }

    return condition;
  });

  merchandising.collections = collectionsList;
  merchandising.conditions = conditionsList;

  // Build disjunctive. Disjunctive value could be:
  // - Boolean (true, false) if merchandising is loaded from DB
  // - Array ([true | false]) if merchandising is reloaded after saving to DB
  if (_.isArray(disjunctive) && disjunctive.length > 0 && _.isBoolean(disjunctive[0])) {
    merchandising.disjunctive = [disjunctive[0]];
  } else if (_.isBoolean(disjunctive)) {
    merchandising.disjunctive = [disjunctive];
  } else {
    merchandising.disjunctive = false;
  }

  return merchandising;
};

/**
 * Build merchandising before saving
 *
 * @param  {object} merchandising
 * @return {object}
 */
export const buildMerchandising = merchandising => {
  const { id: ruleId, collections, disjunctive, conditions } = merchandising;

  const collectionsList = collections
    ? collections.map(collection => {
        const { value, label, handle } = collection;
        return { id: value, title: label, handle };
      })
    : [];

  // Clone rule
  const rule = _.cloneDeep(merchandising);

  // Convert date object to string
  const conditionsList = conditions.map(condition => {
    const { field, relation, value } = condition;
    if (isRelatedToDateAttribute(field)) {
      // Convert date object
      const dateToString =
        relation === 'before' ? getUTCDate(value, 0, 0, 0) : getUTCDate(value, 23, 59, 59);
      return { field, relation, value: dateToString };
    }

    if (isRelatedToSelect2Values(field)) {
      const { value: inputValue = '' } = value;
      return { field, relation, value: inputValue };
    }

    return condition;
  });

  // Set order, status if creating of new rule
  if (!ruleId) {
    rule.order = 1;
    rule.status = 'active';
  }

  rule.collections = collectionsList;
  rule.disjunctive = disjunctive[0];
  rule.conditions = conditionsList;

  return rule;
};
